import { Modal, Typography, Tag, Card } from "antd";
import { ChargebackSuggestionsResponse, ChargebackSuggestion } from "@/models/chargeback-suggestions";
import { renderCurrency } from "@/utils";

const { Paragraph, Title, Text } = Typography;

interface SuggestionsModalProps {
  visible: boolean;
  onClose: () => void;
  content: ChargebackSuggestionsResponse | null;
}

const PriorityColors = {
  high: "#d32f2f",
  medium: "#f57c00",
  low: "#1976d2",
};

function SuggestionCard({ suggestion, priority }: { suggestion: ChargebackSuggestion; priority: "high" | "medium" | "low" }) {
  const color = PriorityColors[priority];

  return (
    <Card
      style={{
        marginBottom: 16,
        borderLeft: `4px solid ${color}`,
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <Title level={4} style={{ margin: 0, color }}>
          {suggestion.issueType}
        </Title>
        <div style={{ marginTop: 8 }}>
          <Text strong>Total Impact: </Text>
          <Text>{renderCurrency(suggestion.totalImpact)}</Text>
        </div>
        {suggestion.primaryOffendingAsins.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <Text strong>Primary Offending ASINs: </Text>
            {suggestion.primaryOffendingAsins.map((asin, idx) => (
              <Tag key={idx} style={{ marginLeft: 4 }}>
                {asin}
              </Tag>
            ))}
          </div>
        )}
      </div>

      {suggestion.amazonRequirement && (
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: "block", marginBottom: 4 }}>
            Amazon Requirement:
          </Text>
          <Paragraph style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}>
            {suggestion.amazonRequirement}
          </Paragraph>
        </div>
      )}

      {suggestion.technicalImplementation.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: "block", marginBottom: 8 }}>
            Technical Implementation:
          </Text>
          {suggestion.technicalImplementation.map((item, idx) => (
            <div key={idx} style={{ marginBottom: 8 }}>
              <Text strong style={{ color }}>
                • {item.title}
              </Text>
              <Paragraph style={{ marginTop: 4, marginBottom: 0, whiteSpace: "pre-wrap" }}>
                {item.description}
              </Paragraph>
            </div>
          ))}
        </div>
      )}

      {suggestion.actionPlan.length > 0 && (
        <div>
          <Text strong style={{ display: "block", marginBottom: 8 }}>
            Action Plan:
          </Text>
          {suggestion.actionPlan.map((item, idx) => (
            <div key={idx} style={{ marginBottom: 8 }}>
              <Text strong style={{ color }}>
                • {item.title}
              </Text>
              <Paragraph style={{ marginTop: 4, marginBottom: 0, whiteSpace: "pre-wrap" }}>
                {item.description}
              </Paragraph>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function PrioritySection({
  title,
  suggestions,
  priority,
}: {
  title: string;
  suggestions: ChargebackSuggestion[];
  priority: "high" | "medium" | "low";
}) {
  if (suggestions.length === 0) {
    return null;
  }

  const color = PriorityColors[priority];

  return (
    <div style={{ marginBottom: 24 }}>
      <Title level={3} style={{ color, marginBottom: 16 }}>
        {title}
      </Title>
      {suggestions.map((suggestion, idx) => (
        <SuggestionCard key={idx} suggestion={suggestion} priority={priority} />
      ))}
    </div>
  );
}

export function SuggestionsModal({
  visible,
  onClose,
  content,
}: SuggestionsModalProps) {
  return (
    <Modal
      title="Chargeback Suggestions"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      style={{ top: 20 }}
    >
      {content ? (
        <div
          style={{
            maxHeight: "70vh",
            overflowY: "auto",
            paddingRight: 8,
          }}
        >
          <PrioritySection
            title="High Priority"
            suggestions={content.highPriority}
            priority="high"
          />
          <PrioritySection
            title="Medium Priority"
            suggestions={content.mediumPriority}
            priority="medium"
          />
          <PrioritySection
            title="Low Priority"
            suggestions={content.lowPriority}
            priority="low"
          />
          {content.highPriority.length === 0 &&
            content.mediumPriority.length === 0 &&
            content.lowPriority.length === 0 && (
              <Paragraph>No suggestions available</Paragraph>
            )}
        </div>
      ) : (
        <Paragraph>No suggestions available</Paragraph>
      )}
    </Modal>
  );
}

